import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert } from '../alert';
import { AlertDescription } from '../alert/alert.description';
import { AlertTitle } from '../alert/alert.title';
import { AlertLevel } from '../alert/types';

export interface ToastData {
  id: string;
  title: string;
  description?: string;
  level?: AlertLevel;
  timeout?: number;
}

interface ToastContextType {
  addToast: (toast: Omit<ToastData, 'id'>) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timerId = timersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: ToastData = {
        ...toast,
        id,
        timeout: toast.timeout || 5000,
      };

      setToasts((prev) => [...prev, newToast]);

      const timerId = setTimeout(() => {
        removeToast(id);
      }, newToast.timeout);

      timersRef.current.set(id, timerId as unknown as number);
    },
    [removeToast],
  );

  useEffect(() => {
    return () => {
      for (const timerId of timersRef.current.values()) {
        clearTimeout(timerId);
      }
      timersRef.current.clear();
    };
  }, []);

  const contextValue = React.useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm" aria-label="Notifications">
        {toasts.map((toast) => (
          <div key={toast.id} className="animate-in slide-in-from-right-full duration-300">
            <Alert level={toast.level || AlertLevel.Info} border>
              <AlertTitle>{toast.title}</AlertTitle>
              {toast.description && <AlertDescription>{toast.description}</AlertDescription>}
            </Alert>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
